import { useEffect, useState } from "react";
import { api } from "./lib/api";
import { Prices } from "./Prices";
import { CallLengths } from "./CallLengths";
import { SalesTax } from "./SalesTax";

// ── THE MONEY PAGE (0364) ───────────────────────────────────────────────────────────────────────
//
// Caleb, 2026-10-23: "as that money's coming in ... I have a healthy amount of it like over in" the
// texting provider -- and "no text ever loses money and no call ever loses money." So: what campaigns
// hold (services Orator still owes), what came in and went out, what it cost Orator, what the card
// fees took, and the provider balance against the safety line the API checks every five minutes.
// Card fees are an ESTIMATE (2.9% + 30¢ a card payment); Stripe's dashboard has the exact figure.

type Window = { paid_in_cents: number; card_cents: number; card_payments: number; card_fees_est_cents: number;
  used_cents: number; cost_cents: number; margin_cents: number; refunded_cents: number; sales_tax_cents?: number };
type Row = { campaign_id: string; name: string; balance_cents: number; auto_topup: boolean; card_last4: string | null;
  texting: string | null; monthly_paid_through: string | null; phone_number: string | null; last_paid_at: string | null };
type Money = { held_cents: number; this_month: Window; all_time: Window; safety_line_cents: number;
  provider_available_cents: number | null; provider_error: string | null; provider_test_mode: boolean; campaigns: Row[];
};

const $ = (c: number | null | undefined) => c == null ? "—" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function MoneyTab() {
  const [m, setM] = useState<Money | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const load = () => {
    setBusy(true); setErr(null);
    api<Money>("/staff/money").then(setM).catch((e) => setErr(String(e?.message ?? e))).finally(() => setBusy(false));
  };
  useEffect(load, []);
  const short = m && m.provider_available_cents != null && m.provider_available_cents < m.safety_line_cents;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold">Money</h2>
        <button onClick={load} disabled={busy} className="text-sm rounded-lg border border-border px-3 py-1 disabled:opacity-50">
          {busy ? "Loading…" : "Refresh"}
        </button>
      </div>
      {err && <div className="err">Couldn't load. {err}</div>}
      {m && (<>
        <div className={`rounded-xl border px-4 py-3 text-sm ${short ? "border-red-300 bg-red-50 text-red-800" : "border-border"}`}>
          <div className="font-bold">Texting and calling provider balance: {m.provider_test_mode ? "test mode (not connected)" : $(m.provider_available_cents)}</div>
          <div>Safety line: {$(m.safety_line_cents)} (the larger of the floor and 3 days of recent use). Campaigns hold {$(m.held_cents)} of prepaid credit.</div>
          {m.provider_error && <div>Couldn't read the balance: {m.provider_error}</div>}
          {short && <div className="font-bold mt-1">Under the safety line. Add funds or check auto-recharge now.</div>}
        </div>
        <Prices />
        <CallLengths />
        <SalesTax />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {([["This month", m.this_month], ["All time", m.all_time]] as const).map(([label, w]) => (
            <div key={label} className="rounded-xl border border-border px-4 py-3 text-sm">
              <div className="font-bold mb-1.5">{label}</div>
              <table className="w-full"><tbody>
                <tr><td className="text-muted">Paid in (cards, top-ups, checks)</td><td className="text-right">{$(w.paid_in_cents)}</td></tr>
                <tr><td className="text-muted">Card fees (estimate, {w.card_payments} payments)</td><td className="text-right">−{$(w.card_fees_est_cents)}</td></tr>
                <tr><td className="text-muted">Charged to campaigns</td><td className="text-right">{$(w.used_cents)}</td></tr>
                <tr><td className="text-muted">What that cost Orator</td><td className="text-right">−{$(w.cost_cents)}</td></tr>
                <tr><td className="text-muted">Refunded or disputed</td><td className="text-right">−{$(w.refunded_cents)}</td></tr>
                <tr className="font-bold"><td>Margin on use, after card fees</td><td className="text-right">{$(w.margin_cents)}</td></tr>
                <tr><td className="text-muted">Sales tax collected (owed to Texas, not margin)</td><td className="text-right">{$(w.sales_tax_cents ?? 0)}</td></tr>
              </tbody></table>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead><tr className="text-left text-muted border-b border-border">
              <th className="py-2 pr-4 font-medium">Campaign</th><th className="py-2 pr-4 font-medium">Credit</th>
              <th className="py-2 pr-4 font-medium">Top-up</th><th className="py-2 pr-4 font-medium">Texting</th>
              <th className="py-2 pr-4 font-medium">Paid through</th><th className="py-2 pr-4 font-medium">Last paid in</th>
            </tr></thead>
            <tbody>{m.campaigns.map((r) => (
              <tr key={r.campaign_id} className="border-b border-border/60">
                <td className="py-2 pr-4 font-medium">{r.name}</td>
                <td className={`py-2 pr-4 ${r.balance_cents <= 0 ? "text-red-700 font-bold" : ""}`}>{$(r.balance_cents)}</td>
                <td className="py-2 pr-4">{r.auto_topup ? `on${r.card_last4 ? ` · ${r.card_last4}` : ""}` : <span className="text-amber-700">off</span>}</td>
                <td className="py-2 pr-4">{r.texting ?? "—"}{r.phone_number ? ` · ${r.phone_number}` : ""}</td>
                <td className="py-2 pr-4">{r.monthly_paid_through ?? "—"}</td>
                <td className="py-2 pr-4">{r.last_paid_at ? r.last_paid_at.slice(0, 10) : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
          {m.campaigns.length === 0 && <div className="text-sm text-muted py-3">No campaign has bought credit or started texting yet.</div>}
        </div>
      </>)}
    </div>
  );
}
